import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DatePicker, TimePicker, DateTimePicker } from '@material-ui/pickers';

import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';
import Fab from '@material-ui/core/Fab';
import IconButton from '@material-ui/core/IconButton';
import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';
import CircularProgress from '@material-ui/core/CircularProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import InputAdornment from '@material-ui/core/InputAdornment';
import Alert from '@material-ui/lab/Alert';

import PublishIcon from '@material-ui/icons/Publish';
import CloseIcon from '@material-ui/icons/Close';
import EmailIcon from '@material-ui/icons/Email';
import PaymentIcon from '@material-ui/icons/Payment';

import SearchUsersAutocomplete from 'app/components/user/UserSearchAutocomplete';
import VaporExtension from 'app/utils/VaporExtension';
import FileTypes from 'app/utils/FileTypes';
import parseCurrencyField from 'app/utils/ParseCurrencyField';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginTopSmall: {
        marginTop: theme.spacing(1)
    },
    field: {
        width: '100%',
        padding: theme.spacing(3)
    },
    uploadContainer: {
        textAlign: 'center',
        position: 'relative',
    },
    extendedIcon: {
        marginRight: theme.spacing(1),
    },
    buttonProgress: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    image: {
        width: '100%',
        maxWidth: '400px',
        display: 'block',
        margin: '0 auto'
    },
    error: {
        color: theme.palette.error.main
    }
}));

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

export default function Field(props) {

    const {
        field,
        fields,
        value,
        values,
        handleChange,
        error,
        errorMessage,
        admin,
        openPaymentForm
    } = props;

    const api = useApi();
    const classes = useStyles();

    const [ file, setFile ] = useState(null);
    const [ uploadProgress, setUploadProgress ] = useState(0);
    const [ fileLoading, setFileLoading ] = useState(false);
    const [ users, setUsers ] = useState([]);
    const [ localError, setlocalError ] = useState(false);

    // File Handling -------------------------------------------------

    useEffect(() => {
        //console.log(value ?? null);
        if (field.options.type === 'upload' && value?.file) setFile(value.file);
        if (field.options.type === 'userselect' && value?.users) setUsers(value.users);
    }, [field, value])

    const uploadFile = (event) => {

        const input = event.target;
        const file = input.files[0];
        const extObject = FileTypes.find(obj => obj.mime == file.type);

        if (! file) return false

        if (file.size > 10485760) {
            setlocalError('The file size is too large. Please use files less than 10MB.');
            input.value = null;
            return false;
        }

        if (! extObject) {
            setlocalError('This file type is not supported.');
            input.value = null;
            return false;
        }

        setlocalError(false);

        const token = api.defaults.headers.common['Authorization'];
        setUploadProgress(1);

        VaporExtension.store(file, {
            progress: progress => setUploadProgress(Math.round(progress * 100))
        }, token).then(response => {
            api.post(`/field/${field.id}/file`, {
                uuid: response.uuid,
                url: response.url,
                key: response.key,
                extension: extObject.ext,
                bucket: response.bucket,
                name: file.name,
                content_type: file.type,
                size: file.size
            }).then(res => {
                handleChange(field.id, { target: { value: res.file.id}});
                setFile(res.file);
            }).catch(error => {

                if (error?.response?.data?.errors) {
                    setlocalError(error.response.data.errors[0]);
                } else {
                    setlocalError('Unable to upload your file at this time. Please try again later. Error Code 157');
                }
            }).finally(() => setUploadProgress(0));
        }).catch(() => {
            setUploadProgress(0);
            setlocalError('Unable to upload your file at this time. Please try again later. Error Code 162');
        }).finally(() => {
            input.value = null
        });
    }

    const downloadFile = () => {
        setFileLoading(true);
        api.get(`/field/${field.id}/file/${file.id}`).finally(() => setFileLoading(false));
    }

    const removeFile = () => {
        handleChange(field.id, { target: { value: 0 }});
        setFile(null);
        //document.getElementById(`file-upload-${field.id}`).value = null;
    }

    const addUser = user => {

        if (users.length >= field.options.length) {
            setlocalError(`Limited to ${field.options.length} users.`);
            return false;
        }

        setlocalError(false);

        const newUsers = [...users];

        newUsers.push(user);

        setUsers(newUsers);
        handleChange(field.id, { target: { value: newUsers.map(u => u.id)}});
    }

    const removeUser = id => {
        setlocalError(false);

        const newUsers = [...users];

        const i = newUsers.findIndex(user => user.id === id)

        newUsers.splice(i,1);

        console.log(newUsers);

        setUsers(newUsers);
        handleChange(field.id, { target: { value: newUsers.map(u => u.id)}});
    }

    const emailAddress = useMemo(() => {

        if (field.options?.email_field != '' && field.options.email_field?.length > 0) {
            
            const emailField = fields.find(f => f.options.label === field.options.email_field);
            if (emailField && values[emailField.id]?.value) {
                return values[emailField.id].value;
            }
        }

        return '';
    }, [values]);

    const sendEmail = () => {
        setFileLoading(true);
        api.get(`/field/${field.id}/email/${emailAddress}`).finally(() => setFileLoading(false));
    }

    const paymentAmount = useMemo(() => {

        if (field.options?.payment_amount_field != '' && field.options.payment_amount_field?.length > 0) {
            
            const paymentAmountField = fields.find(f => f.options.label === field.options.payment_amount_field);
            
            if (paymentAmountField && values[paymentAmountField.id]?.value) {
                return parseCurrencyField(Number(values[paymentAmountField.id].value));
            }
        }

        return null;
    }, [values]);

    const handlePaymentForm = () => {

        setlocalError(false);
        
        // payment amount must be greater than 50 cents
        if (! paymentAmount || paymentAmount < 50) {
            setlocalError('Please enter an amount greater than 50 cents before paying.');
        } else {
            openPaymentForm(paymentAmount, field.options.payment_amount_field);
        }
    }

    const initials = user => user.first.charAt(0) + user.last.charAt(0);

    const visible = useMemo(() => {

        if (field.options?.conditional != '' && field.options.conditional?.length > 0) {
            return Object.keys(values).find(key => values[key].value === field.options.conditional) ? true : false;
        }

        if (!admin && field.options?.admin_view === true) return false;

        return true;
    }, [values]);

    const isImage = useMemo(() => {
        if (file) {
            const FileType = FileTypes.find(obj => obj.ext == file.extension);
            if (FileType.icon === 'Image') return true;
        }
        return false;
    }, [file])

    const isError = Boolean(error);

    const buildField = () => {
        switch (field.options.type) {
            case 'header' :
                return (
                    <Typography variant='h3' align={field.options.align}>
                        {field.options.label}
                    </Typography>
                )
            case 'subheader' :
                return (
                    <Typography variant="h6" align={field.options.align}>
                        {field.options.label}
                    </Typography>
                )
            case 'paragraph' :
                return (
                    <Typography variant="body1" align={field.options.align}>
                        {field.options.label}
                    </Typography>
                )
            case 'divider' :
                return <Divider />
            case 'image':
                return (
                    <div style={{ textAlign: field.options.align }}>
                        <img 
                            style={{ height: 'auto', maxWidth: '100%', width: field.options.image_width + "%" }} 
                            src={field.options.image_url} 
                            alt={field.options.label}
                        />
                    </div>
                )
            case 'textbox' :
            case 'email':
                return (
                    <TextField
                        variant="outlined"
                        label={field.options.label}
                        fullWidth
                        helperText={isError ? errorMessage : field.options.helper_text}
                        required={field.options.required}
                        onChange={e => handleChange(field.id, e)}
                        value={value?.value ?? ""}
                        error={isError}
                        type={field.options.type == 'email' ? 'email' : 'text'}
                        disabled={(!admin && field.options.admin_edit === true)}
                    />
                )
            case 'textarea' :
                return (
                    <TextField
                        label={field.options.label}
                        variant="outlined"
                        fullWidth
                        helperText={isError ? errorMessage : field.options.helper_text}
                        multiline
                        rows={5}
                        required={field.options.required}
                        onChange={e => handleChange(field.id, e)}
                        value={value?.value ?? ""}
                        error={isError}
                        disabled={(!admin && field.options.admin_edit === true)}
                    />
                )
            case 'number' :
                return (
                    <TextField
                        label={field.options.label}
                        type='number'
                        variant="outlined"
                        fullWidth
                        helperText={isError ? errorMessage : field.options.helper_text}
                        required={field.options.required}
                        onChange={e => handleChange(field.id, e)}
                        value={value?.value ?? 0}
                        error={isError}
                        disabled={(!admin && field.options.admin_edit === true)}
                    />
                )
            case 'currency' :
                return (
                    <TextField
                        label={field.options.label}
                        type="number"
                        variant="outlined"
                        fullWidth
                        helperText={isError ? errorMessage : field.options.helper_text}
                        required={field.options.required}
                        onChange={e => handleChange(field.id, e)}
                        value={value?.value ?? 0.00}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            step: "0.01"
                        }}
                        error={isError}
                        disabled={(!admin && field.options.admin_edit === true)}
                    />
                )
            case 'date' :
                return (
                    <DatePicker
                        label={field.options.label}
                        inputVariant="outlined"
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        required={field.options.required}
                        onChange={e => handleChange(field.id, e)}
                        value={value?.value ?? null}
                        format="DD"
                        helperText={isError ? errorMessage : field.options.helper_text}
                        error={isError}
                        disabled={(!admin && field.options.admin_edit === true)}
                    />
                )
            case 'time' :
                return (
                    <TimePicker
                        label={field.options.label}
                        inputVariant="outlined"
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        required={field.options.required}
                        onChange={e => handleChange(field.id, e)}
                        value={value?.value ?? null}
                        helperText={isError ? errorMessage : field.options.helper_text}
                        error={isError}
                        disabled={(!admin && field.options.admin_edit === true)}
                    />
                )
            case 'datetime' :
                return (
                    <DateTimePicker
                        label={field.options.label}
                        inputVariant="outlined"
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        required={field.options.required}
                        onChange={e => handleChange(field.id, e)}
                        value={value?.value ?? null}
                        helperText={isError ? errorMessage : field.options.helper_text}
                        error={isError}
                        disabled={(!admin && field.options.admin_edit === true)}
                    />
                )
            case 'options' :
                return (
                    <TextField
                        label={field.options.label}
                        select
                        variant="outlined"
                        fullWidth
                        required={field.options.required}
                        onChange={e => handleChange(field.id, e)}
                        value={value?.value ?? field.options.options[0]}
                        helperText={isError ? errorMessage : field.options.helper_text}
                        error={isError}
                        disabled={(!admin && field.options.admin_edit === true)}
                        >
                        {field.options.options.map((option, index) => (
                            <MenuItem key={index} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                )
            case 'checkbox' :
                return (
                    <FormControlLabel
                        control={
                        <Checkbox
                            checked={value?.value ?? false}
                            onChange={e => handleChange(field.id, e)}
                            required={field.options.required}
                            className={isError ? classes.error : null}
                        />
                        }
                        label={field.options.label}
                        className={isError ? classes.error : null}
                    />
                )
            case 'upload' :
                return file ? (
                    <div>
                        <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                            {field.options.label}{field.options.required ? ' *' : null}
                        </Typography>
                        {isImage &&
                            <img src={file.full_url} className={classes.image}/> 
                        }
                        <List>
                            <ListItem key={value} role={undefined} dense button onClick={downloadFile} disabled={fileLoading}>
                                <ListItemText id={value.id} primary={file.name} />
                                <ListItemSecondaryAction>
                                    <IconButton edge="end" aria-label="comments" onClick={removeFile} disabled={(!admin && field.options.admin_edit === true)}>
                                        <CloseIcon fontSize="small"/>
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        </List>
                        {isError &&
                            <Alert severity='error'>{errorMessage}</Alert>
                        }
                    </div>
                ) : (
                    <div className={classes.uploadContainer}>
                        <label htmlFor={`file-upload-${field.id}`}>
                            <input
                                hidden
                                id={`file-upload-${field.id}`}
                                name="file-upload"
                                type="file"
                                onChange={uploadFile}
                            />
                            <Tooltip title={field.options.helper_text}>
                                <span>
                                    <Fab
                                        variant="extended"
                                        color='secondary'
                                        component="span"
                                        disabled={((!admin && field.options.admin_edit === true) || uploadProgress > 0)}
                                        >
                                        <PublishIcon className={classes.extendedIcon}/>
                                        {field.options.label}
                                        {field.options.required ? '  *' : null}
                                    </Fab>
                                </span>
                            </Tooltip>
                            {(uploadProgress > 0) &&
                                <CircularProgress
                                    variant="determinate"
                                    value={uploadProgress}
                                    size={24}
                                    className={classes.buttonProgress}
                                />
                            }
                            {(localError || field.options.helper_text) &&
                                <Typography
                                    display="block"
                                    variant="caption"
                                    color={localError ? 'error' : 'textSecondary'}
                                    className={classes.marginTop}
                                    >
                                    {localError ? localError : field.options.helper_text}
                                </Typography>
                            }
                        </label>
                    </div>
                )
            case 'userselect' :
                return (
                    <>
                        <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                            {field.options.label}{field.options.required ? ' *' : null} (Select up to {field.options.length})
                        </Typography>
                        {(users && users.length > 0) && users.map(user => (
                            <CardHeader
                                key={user.id}
                                avatar={
                                    <Avatar
                                        aria-label={user.full_name}
                                        alt={user.full_name}
                                        className={classes.avatar}
                                        src={user.avatar ? user.avatar.full_url : null}
                                        >
                                        {initials(user)}
                                    </Avatar>
                                }
                                title={user.full_name}
                                subheader={user.membership_level}
                                action={
                                    <Tooltip title="Remove User">
                                        <span>
                                            <IconButton
                                                aria-label="Remove User"
                                                onClick={() => removeUser(user.id)}
                                                disabled={(!admin && field.options.admin_edit === true)}
                                                >
                                                <CloseIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                }
                            />
                        ))}
                        {(users.length < field.options.length) &&
                            <SearchUsersAutocomplete
                                closeOnSelect
                                clearOnSelect
                                onSelect={addUser}
                                label='Add a User...'
                                error={isError || localError}
                                disabled={(!admin && field.options.admin_edit === true)}
                                groupId={field.options.group_id}
                            />
                        }
                        <Typography variant="caption" color={(localError || isError) ? 'error' : 'textSecondary'}>
                            {localError ? localError : isError ? errorMessage : field.options.helper_text}
                        </Typography>
                    </>
                )
            case 'emailbutton':
                return (
                    <div className={classes.uploadContainer}>
                        <Tooltip title={field.options.helper_text}>
                            <span>
                                <Fab
                                    variant="extended"
                                    color='secondary'
                                    component="span"
                                    disabled={((!admin && field.options.admin_edit === true))}
                                    onClick={sendEmail}
                                    >
                                    <EmailIcon className={classes.extendedIcon}/>
                                    {field.options.label}
                                    {field.options.required ? '  *' : null}
                                </Fab>
                            </span>
                        </Tooltip>
                        {(localError) &&
                            <Typography
                                display="block"
                                variant="caption"
                                color='error'
                                className={classes.marginTop}
                                >
                                {localError}
                            </Typography>
                        }
                    </div>
                )
            case 'paymentform':
                return (
                    <div className={classes.uploadContainer} id="payment-form-ref">
                        <Tooltip title={field.options.helper_text}>
                            <span>
                                <Fab
                                    variant="extended"
                                    color='secondary'
                                    component="span"
                                    disabled={((!admin && field.options.admin_edit === true) || value?.value)}
                                    onClick={handlePaymentForm}
                                    >
                                    <PaymentIcon className={classes.extendedIcon}/>
                                    {field.options.label}
                                </Fab>
                            </span>
                        </Tooltip>
                        {(localError) &&
                            <Typography
                                display="block"
                                variant="caption"
                                color='error'
                                className={classes.marginTop}
                                >
                                {localError}
                            </Typography>
                        }
                        {(value?.value) &&
                            <Alert severity="success" className={classes.marginTop}>
                                Payment Succeeded. Please make a new entry to pay again.
                            </Alert>
                        }
                        {(isError) &&
                            <Alert severity='error' className={classes.marginTop}>{errorMessage}</Alert>
                        }
                    </div>
                )
        }
    }

    return visible ? (
        <Grid item xs={12}>
            {buildField(field, value, handleChange)}
        </Grid>
    ) : null;
}
